import { VariantProps } from "class-variance-authority";
import { Hand } from "lucide-react";
import { forwardRef } from "react";
import { toast } from "sonner";

import { getCurrentParticipantSelector } from "@/features/participants/api/get-current-participant";
import { lowerHand, raiseHand } from "@/features/raise-hand/api/toggle-hand";
import { useVidyoStore } from "@/features/vidyo-call/utils/vidyo-store";

import { ActionButton, ActionButtonProps } from "../action-button";

type RaiseHandProps = Omit<ActionButtonProps, "Icon" | "text"> &
  VariantProps<typeof ActionButton> & {
    variant?: "default" | "ghost" | "destructive";
    size?: "fixed" | "auto";
  };

export const RaiseHand = forwardRef<HTMLButtonElement, RaiseHandProps>(
  ({ variant = "default", size = "fixed", ...props }, ref) => {
    const isHandRaised =
      useVidyoStore(getCurrentParticipantSelector)?.isHandRaised || false;
    const isWebsocketAuthenticated = useVidyoStore(
      (state) => state.webSocket.isAuthenticated,
    );

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      try {
        if (isHandRaised) {
          lowerHand();
        } else {
          raiseHand();
        }
      } catch {
        toast.error("Failed to raise hand");
      }

      // Call the original onClick handler if it exists,
      // this closes the dropdown menu
      props.onClick?.(event);
    };

    if (!isWebsocketAuthenticated) {
      return null;
    }

    return (
      <ActionButton
        {...props}
        ref={ref}
        variant={variant}
        size={size}
        onClick={handleClick}
        Icon={Hand}
        text={isHandRaised ? "Lower Hand" : "Raise Hand"}
        isActive={isHandRaised}
      />
    );
  },
);

RaiseHand.displayName = "RaiseHand";
